/**
 * recharge-custom.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Registers <recharge-custom-block> custom element.
 *
 * Responsibilities:
 *   1. Tab switching (Subscribe ↔ One-time)
 *   2. Selling plan dropdown deduplication by plan NAME (stable identifier)
 *   3. Correct plan ID lookup per selected variant using Dawn's existing
 *      data-selected-variant JSON ({{ variant | json }} includes
 *      selling_plan_allocations — no extra Liquid block needed)
 *
 * Variant change sync uses Dawn's PUB_SUB_EVENTS.variantChange ('variant-change')
 * which fires AFTER product-info.js completes the section render and updates
 * data-selected-variant. This avoids any timing issues.
 *
 * Everything else is Dawn's:
 *   Variant change   → <variant-selects>  (global.js)
 *   Section render   → product-info.js    (on every variant click)
 *   Cart add         → <product-form>     (product-form.js → /cart/add.js → cart drawer)
 *   Gallery/price    → product-info.js
 *
 * Note on button loading state: Dawn's product-info.js disables the submit
 * button during every variant-change section render (resetProductFormState),
 * then re-enables it after the fetch completes. This is standard Dawn behaviour
 * and identical to the default product page. The render speed depends on
 * Shopify's server response time for the section.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// Capture native scroll methods once at module load — before any override
const _rcNativeScrollTo  = window.scrollTo.bind(window);
const _rcNativeSIV       = HTMLElement.prototype.scrollIntoView;

if (!customElements.get('recharge-custom-block')) {
  customElements.define(
    'recharge-custom-block',
    class RechargeCustomBlock extends HTMLElement {

      connectedCallback() {
        try {
          this._tabs       = this.querySelectorAll('[data-tab]');
          this._subContent = this.querySelector('.rc-subscribe-content');
          this._planSelect = this.querySelector('.rc-plan-select');
          this._planInput  = this.querySelector('.rc-plan-input');

          if (!this._tabs.length || !this._subContent) return;

          // ── Deduplicate dropdown by display name ───────────────────────────
          // Recharge assigns a different plan ID per variant for the same plan
          // name (e.g. "Prepay for 3 Boxes" has one ID per variant).
          // We keep the first occurrence of each name visually; the correct
          // variant-specific ID is resolved by _syncPlanInput() at runtime.
          if (this._planSelect) {
            const seen = new Set();
            Array.from(this._planSelect.options).forEach(opt => {
              if (seen.has(opt.text.trim())) {
                opt.remove();
              } else {
                seen.add(opt.text.trim());
              }
            });
          }

          // ── Tab click listeners ────────────────────────────────────────────
          this._tabs.forEach(tab =>
            tab.addEventListener('click', () => this._setTab(tab.dataset.tab))
          );

          // ── Re-sync when plan dropdown selection changes ───────────────────
          if (this._planSelect) {
            this._planSelect.addEventListener('change', () => this._syncPlanInput());
          }

          // ── Freeze scroll position during variant re-render ───────────────
          // When a variant radio changes, Dawn fetches a new section render,
          // swaps the DOM, updates the media gallery, and calls .focus() on
          // the radio — any of these can scroll the page.
          //
          // Nuclear fix: intercept window.scrollTo entirely for 1.5s after
          // a radio change, then restore the saved scroll position.
          // This blocks every scroll call regardless of who makes it.
          this.querySelectorAll('input[type="radio"]').forEach(input => {

            // Prevent focus() from scrolling (Dawn calls this after render)
            input.focus = function(opts) {
              HTMLElement.prototype.focus.call(this, { ...opts, preventScroll: true });
            };

            // Save position the moment the user touches the input
            const saveY = () => { this._savedScrollY = window.scrollY; };
            input.addEventListener('mousedown', saveY);
            input.addEventListener('touchstart', saveY, { passive: true });

            input.addEventListener('change', () => {
              const y = this._savedScrollY ?? window.scrollY;

              // Block ALL scroll calls for 1.5s using the natives
              // captured at module load — never captures an override
              window.scrollTo = function() {};
              HTMLElement.prototype.scrollIntoView = function() {};

              setTimeout(() => {
                window.scrollTo = _rcNativeScrollTo;
                HTMLElement.prototype.scrollIntoView = _rcNativeSIV;
                _rcNativeScrollTo({ top: y, behavior: 'instant' });
              }, 1500);
            });
          });

          // ── Subscribe to Dawn's variant-change PubSub event ───────────────
          // 'variant-change' = PUB_SUB_EVENTS.variantChange (constants.js).
          // Fires after product-info.js finishes the section render fetch AND
          // after updateOptionValues() has refreshed data-selected-variant.
          // This is the correct moment to read the new variant's plan IDs.
          // The subscribe() function comes from Dawn's pubsub.js (global).
          if (typeof subscribe === 'function') {
            this._variantUnsub = subscribe('variant-change', () => {
              this._syncPlanInput();
            });
          }

          // ── Subscribe tab is the default on page load ──────────────────────
          this._setTab('subscribe');

        } catch (err) {
          console.warn('[recharge-custom]', err);
        }
      }

      disconnectedCallback() {
        // Clean up PubSub subscription when element is removed from DOM
        this._variantUnsub?.();
      }

      /**
       * Set rc-plan-input to the correct selling plan ID for the currently
       * selected variant + selected plan name.
       *
       * Reads from Dawn's [data-selected-variant] JSON which is output by
       * {{ variant | json }} in Liquid. Shopify's variant JSON object includes
       * selling_plan_allocations, each with selling_plan.id and selling_plan.name.
       * product-info.js refreshes this JSON after every section render.
       *
       * Per Recharge docs: plan IDs differ per variant — always resolve at
       * runtime using plan NAME as the stable identifier.
       */
      _syncPlanInput() {
        if (!this._planInput || this._planInput.disabled || !this._planSelect) return;

        const planName = this._planSelect.options[this._planSelect.selectedIndex]?.text?.trim();
        if (!planName) {
          this._planInput.value = this._planSelect.value;
          return;
        }

        try {
          const variantJson = this.querySelector('[data-selected-variant]')?.textContent;
          if (variantJson) {
            const variant = JSON.parse(variantJson);
            const alloc = variant?.selling_plan_allocations?.find(
              (a) => a.selling_plan?.name?.trim() === planName
            );
            if (alloc?.selling_plan?.id != null) {
              this._planInput.value = alloc.selling_plan.id;
              return;
            }
          }
        } catch (_) { /* malformed JSON — fall through to fallback */ }

        // Fallback: use the <option value> attribute
        // (first plan ID for this name from selling_plan_groups)
        this._planInput.value = this._planSelect.value;
      }

      _setTab(name) {
        const isSub = name === 'subscribe';

        // Update tab button states
        this._tabs.forEach(tab => {
          const active = tab.dataset.tab === name;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
        });

        // Show/hide subscribe-only content
        this._subContent.hidden = !isSub;

        // Toggle selling_plan input
        // disabled = excluded from FormData = one-time purchase (W3C spec)
        // enabled  = included in FormData  = subscription purchase
        if (this._planInput) {
          this._planInput.disabled = !isSub;
          if (isSub) {
            this._syncPlanInput();
          } else {
            this._planInput.value = '';
          }
        }
      }
    }
  );
}
